import { PARTNER_KEYS, getShowOldClalit } from "../utils/partner";

/**
 * V2 screen key constants — single source of truth for screen names.
 * Use these instead of raw strings when calling isV2Enabled or passing
 * `screen` to LegacyHeaderGate / ScreenHeaderGate.
 */
export const V2_SCREEN_KEYS = {
  FIT_EXTEND_SELECTOR: "fitExtendSelector",
  VENUE: "venue",
  SELECT_DATE_TIME: "selectDateTime", // V2 component: phoneCoordination
  SUMMARY: "summary", // V2 component: purchaseSummary
  MULTIPLE_ENTRY_PASS_PURCHASE: "multipleEntryPassPurchase", // V2 component: passPurchase
  SELECT_ROOM_BY_DATE: "selectRoomByDate", // V2 component: lessonCalendar
  ORDER_CONFIRMATION: "orderConfirmation",
  ORDER_HISTORY: "orderHistory",
  MULTIPLE_ENTRY_PASS: "multipleEntryPass",
  MULTIPLE_ENTRY_PASS_DETAILS: "multipleEntryPassDetails",
  CONNECTION_ERROR: "connectionError",
  PROFILE: "profile",
  ADDRESS_SELECT: "addressSelect",
  CARD_DETAILS: "cardDetails",
  ADD_CARD: "addCard",
  WELCOME: "welcome",
  HOME: "home", // gated inside screens/mainPage.jsx
  HOW_IT_WORKS: "howItWorks",
  DISCOVER_PASS_CARD: "discoverPassCard",
  MAP: "map",
  FAQ: "faq",
  TERMS_EXTERNAL: "termsExternal",
  REGISTRATION: "registration", // V2 component: addEmail
  LOCATION_REQUIRED: "locationRequired",
  USER_PHONE_VALIDATION: "userPhoneValidation", // V2 component: phoneValidation
  OTP_VALIDATION: "otpValidation",
  ERROR_POPUP: "errorPopup", // global error dialog replacing the legacy Swal popup (helpers/swalErrorPopup.js)
};

// Screens rebuilt with the V2 design, and which partners see them.
// Rolling a screen out to another partner = add its key here + visual QA.
export const V2_SCREENS = {
  [V2_SCREEN_KEYS.FIT_EXTEND_SELECTOR]: [PARTNER_KEYS.CLALIT],
  [V2_SCREEN_KEYS.VENUE]: [PARTNER_KEYS.CLALIT],
  [V2_SCREEN_KEYS.SELECT_DATE_TIME]: [PARTNER_KEYS.CLALIT],
  [V2_SCREEN_KEYS.SUMMARY]: [PARTNER_KEYS.CLALIT],
  [V2_SCREEN_KEYS.MULTIPLE_ENTRY_PASS_PURCHASE]: [PARTNER_KEYS.CLALIT],
  [V2_SCREEN_KEYS.SELECT_ROOM_BY_DATE]: [PARTNER_KEYS.CLALIT],
  [V2_SCREEN_KEYS.ORDER_CONFIRMATION]: [PARTNER_KEYS.CLALIT],
  [V2_SCREEN_KEYS.ORDER_HISTORY]: [PARTNER_KEYS.CLALIT],
  [V2_SCREEN_KEYS.MULTIPLE_ENTRY_PASS]: [PARTNER_KEYS.CLALIT], // header-only V2 for now (body still legacy)
  [V2_SCREEN_KEYS.MULTIPLE_ENTRY_PASS_DETAILS]: [PARTNER_KEYS.CLALIT],
  [V2_SCREEN_KEYS.CONNECTION_ERROR]: [PARTNER_KEYS.CLALIT], // route-level screen (/connection-error), no V1 counterpart
  [V2_SCREEN_KEYS.PROFILE]: [PARTNER_KEYS.CLALIT], // route-level screen (/profile), reached via the hamburger; no V1 counterpart
  [V2_SCREEN_KEYS.ADDRESS_SELECT]: [PARTNER_KEYS.CLALIT], // route-level screen (/address-select), no V1 counterpart
  [V2_SCREEN_KEYS.CARD_DETAILS]: [PARTNER_KEYS.CLALIT], // header-only V2 for now (body still legacy)
  [V2_SCREEN_KEYS.ADD_CARD]: [PARTNER_KEYS.CLALIT], // header-only V2 for now (body still legacy)
  [V2_SCREEN_KEYS.WELCOME]: [PARTNER_KEYS.CLALIT], // the intro + location gate, routed at /welcome (end of the first onboarding)
  [V2_SCREEN_KEYS.HOME]: [PARTNER_KEYS.CLALIT], // route "/home", gated inside screens/mainPage.jsx
  [V2_SCREEN_KEYS.HOW_IT_WORKS]: [PARTNER_KEYS.CLALIT], // route-level screen (/how-it-works), reached from profile + discover-pass-card; no V1 counterpart
  [V2_SCREEN_KEYS.DISCOVER_PASS_CARD]: [PARTNER_KEYS.CLALIT], // route-level screen (/discover-pass-card), pass promo; reached from the buy-a-pass banners on home, profile, and purchase summary
  [V2_SCREEN_KEYS.MAP]: [PARTNER_KEYS.CLALIT], // header-only V2 for now (body still legacy)
  [V2_SCREEN_KEYS.FAQ]: [PARTNER_KEYS.CLALIT], // header-only V2 for now (body still legacy)
  [V2_SCREEN_KEYS.TERMS_EXTERNAL]: [PARTNER_KEYS.CLALIT], // header-only V2 for now (body still legacy)
  [V2_SCREEN_KEYS.REGISTRATION]: [PARTNER_KEYS.CLALIT], // route "/registration", gated inside screens/registration.jsx
  [V2_SCREEN_KEYS.LOCATION_REQUIRED]: [PARTNER_KEYS.CLALIT], // route-level screen (/location-required), no V1 counterpart
  [V2_SCREEN_KEYS.USER_PHONE_VALIDATION]: [PARTNER_KEYS.CLALIT], // route "/phone-validation", gated inside screens/userPhoneValidation.jsx
  [V2_SCREEN_KEYS.OTP_VALIDATION]: [PARTNER_KEYS.CLALIT], // route "/otp-validation", gated inside screens/otpValidation.jsx
  [V2_SCREEN_KEYS.ERROR_POPUP]: [PARTNER_KEYS.CLALIT], // not a screen: the app-wide error dialog (v2/components/ErrorDialogHost.jsx)
};

/**
 * Screens the ShowOldClalit kill switch does NOT turn off.
 *
 * These seven are route-level V2 screens with no V1 counterpart, so "old flow" has nothing to
 * fall back to — each self-guards with `<Navigate to="/" replace />`, and letting the switch
 * flip that guard would bounce a user out mid-task (worst case: AddressSelect, whose guard runs
 * during render, so an address being typed is lost silently). They stay reachable in both
 * states; the old flow simply never links to them.
 */
const FLAG_EXEMPT_SCREENS = new Set([
  V2_SCREEN_KEYS.WELCOME,
  V2_SCREEN_KEYS.PROFILE,
  V2_SCREEN_KEYS.LOCATION_REQUIRED,
  V2_SCREEN_KEYS.ADDRESS_SELECT,
  V2_SCREEN_KEYS.HOW_IT_WORKS,
  V2_SCREEN_KEYS.DISCOVER_PASS_CARD,
  V2_SCREEN_KEYS.CONNECTION_ERROR,
]);

/**
 * Whether the V2 design is active for this screen and partner.
 *
 * The server-driven ShowOldClalit switch (Getuserdata → localStorage, see
 * utils/partner.getShowOldClalit) is read HERE rather than at the ~33 call sites: it is a blanket
 * kill switch, so one choke point covers every screen and all three integration mechanisms
 * (in-screen gate, LegacyHeaderGate/ScreenHeaderGate, route-level self-guards).
 *
 * Read synchronously from localStorage, not from the store, because most call sites pass a bare
 * `useSelector(selectPartner)` and this runs during the first painted frame — before the
 * handshake dispatches anything. selectShowOldClalit exists for components that want to
 * re-render when it changes mid-session.
 */
export const isV2Enabled = (screen, partner) => {
  const enabledForPartner = V2_SCREENS[screen]?.includes(partner) ?? false;
  if (!enabledForPartner) return false;

  if (FLAG_EXEMPT_SCREENS.has(screen)) return true;

  return !getShowOldClalit();
};
